import React, { useState, useMemo, useEffect } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Button,
  Tooltip,
  Snackbar,
} from '@mui/material';
import { ThumbUp, ThumbDown, Search, LinkedIn, Visibility, RateReview, Email, AccessTime } from '@mui/icons-material';
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfDay,
  endOfDay,
  isWithinInterval,
  parseISO,
  differenceInHours
} from 'date-fns';
import fr from 'date-fns/locale/fr';
import { PeriodFilter as PeriodFilterType } from '../types/rdv';
import PeriodFilter from '../components/PeriodFilter';
import { getLeadsWithRdv, updateRdvStatus } from '../lib/supabase/leadsService';
import { Database, EtatRDV } from '../types/supabase';
import { useAuth } from '../lib/supabase/AuthContext';
import QualificationModal from '../components/QualificationModal';
import QualificationActionModal from '../components/QualificationActionModal';

type Lead = Database['public']['Tables']['leads']['Row'];

// Fonction pour vérifier si un RDV a dépassé les 24h
const isRdvExpired = (rdvDate: string): boolean => {
  const rdvDateTime = new Date(rdvDate);
  const now = new Date();
  const hoursDifference = Math.floor((now.getTime() - rdvDateTime.getTime()) / (1000 * 60 * 60));
  return hoursDifference > 24;
};

// Conversion d'un Lead en RDV pour l'affichage
const leadToRdvDisplay = (lead: Lead) => {
  return {
    id: lead.id,
    etat: lead.etat_rdv || 'En attente',
    prenom: lead.prenom,
    nom: lead.nom,
    entreprise: lead.entreprise,
    date: lead.rdv_date || new Date().toISOString(),
    telephone: lead.telephone || '',
    email: lead.email || '',
    linkedin: lead.linkedin || '',
    qualification: lead.rdv_commentaire || '',
    retourClient: lead.retour_client || '',
    source: lead.rdv_source || 'Mail'
  };
};

const RDVPage: React.FC = () => {
  const { user } = useAuth();
  const [etatFilter, setEtatFilter] = useState<'tous' | EtatRDV>('tous');
  const [period, setPeriod] = useState<PeriodFilterType>('tous');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // État pour la modale de qualification
  const [selectedRdv, setSelectedRdv] = useState<ReturnType<typeof leadToRdvDisplay> | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // État pour la modale de qualification avancée (Validé, Litige, No show)
  const [selectedRdvForAction, setSelectedRdvForAction] = useState<ReturnType<typeof leadToRdvDisplay> | null>(null);
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  
  // État pour stocker les leads avec rdv=true
  const [rdvLeads, setRdvLeads] = useState<Lead[]>([]);

  // Fonction pour charger les leads avec rdv=true depuis Supabase
  const fetchRdvLeads = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await getLeadsWithRdv(user.id);
      
      if (error) {
        throw error;
      }
      
      setRdvLeads(data || []);
    } catch (err: any) {
      console.error('Erreur lors du chargement des RDV:', err);
      setError(`Une erreur est survenue: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Charger les leads avec rdv=true depuis Supabase
  useEffect(() => {
    fetchRdvLeads();
  }, [user]);

  const handleDateChange = (start: Date | null, end: Date | null) => {
    setStartDate(start);
    setEndDate(end);
  };

  // Transformer les leads en format d'affichage et appliquer les filtres
  const filteredRDVs = useMemo(() => {
    // Convertir les leads en format d'affichage pour le tableau
    const rdvDisplayItems = rdvLeads.map(leadToRdvDisplay);
    
    let filtered = [...rdvDisplayItems];

    // Filtre de recherche
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(rdv => 
        rdv.prenom.toLowerCase().includes(searchLower) ||
        rdv.nom.toLowerCase().includes(searchLower) ||
        rdv.entreprise.toLowerCase().includes(searchLower) ||
        rdv.qualification.toLowerCase().includes(searchLower)
      );
    }

    // Filtre par état
    if (etatFilter !== 'tous') {
      filtered = filtered.filter(rdv => rdv.etat === etatFilter);
    }

    // Filtre par période
    if (period === 'tous') {
      return filtered;
    }

    const now = new Date();
    let dateRange: { start: Date; end: Date };

    switch (period) {
      case 'jour':
        dateRange = {
          start: startOfDay(now),
          end: endOfDay(now),
        };
        break;
      case 'semaine':
        dateRange = {
          start: startOfWeek(now, { locale: fr }),
          end: endOfWeek(now, { locale: fr }),
        };
        break;
      case 'mois':
        dateRange = {
          start: startOfMonth(now),
          end: endOfMonth(now),
        };
        break;
      case 'personnalise':
        if (startDate && endDate) {
          dateRange = {
            start: startDate,
            end: endDate,
          };
        } else {
          return filtered;
        }
        break;
      default:
        return filtered;
    }

    return filtered.filter(rdv => {
      const rdvDate = new Date(rdv.date);
      return isWithinInterval(rdvDate, dateRange);
    });
  }, [rdvLeads, etatFilter, period, startDate, endDate, searchTerm]);

  // Ouvrir la modale de qualification pour un RDV spécifique
  const handleOpenQualificationModal = (rdv: ReturnType<typeof leadToRdvDisplay>) => {
    setSelectedRdv(rdv);
    setIsModalOpen(true);
  };
  
  // Fermer la modale de qualification
  const handleCloseQualificationModal = () => {
    setIsModalOpen(false);
    setSelectedRdv(null);
  };
  
  // Gérer la mise à jour de la qualification depuis la modale
  const handleQualificationUpdated = (id: string, newQualification: string) => {
    // Mettre à jour l'état local
    setRdvLeads(prev => prev.map(lead => {
      if (lead.id === id) {
        return {
          ...lead,
          rdv_commentaire: newQualification
        };
      }
      return lead;
    }));
  };

  // Mettre à jour le statut de qualification dans Supabase
  const handleQualification = async (rdv: ReturnType<typeof leadToRdvDisplay>, newEtat: 'Validé' | 'Disqualifié') => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      const { error } = await updateRdvStatus(
        rdv.id,
        newEtat
      );
      
      if (error) {
        throw error;
      }
      
      // Mettre à jour l'état local après la mise à jour réussie dans Supabase
      setRdvLeads(prev => prev.map(lead => {
        if (lead.id === rdv.id) {
          return {
            ...lead,
            etat_rdv: newEtat
          };
        }
        return lead;
      }));
      
    } catch (err: any) {
      console.error('Erreur lors de la qualification du RDV:', err);
      setError(`Une erreur est survenue: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Ouvrir la modale de qualification avancée pour un RDV spécifique
  const handleOpenQualificationActionModal = (rdv: ReturnType<typeof leadToRdvDisplay>) => {
    setSelectedRdvForAction(rdv);
    setIsActionModalOpen(true);
  };
  
  // Fermer la modale de qualification avancée
  const handleCloseQualificationActionModal = () => {
    setIsActionModalOpen(false);
    setSelectedRdvForAction(null);
  };
  
  // Rafraîchir les données après qualification
  const handleQualificationActionCompleted = () => {
    // Recharger les données de RDV
    if (user) {
      fetchRdvLeads();
    }
  };

  const getEtatColor = (etat: string) => {
    switch (etat) {
      case 'Validé':
        return 'success';
      case 'Disqualifié':
        return 'error';
      case 'Litige':
        return 'error';
      case 'No show':
        return 'error';
      default:
        return 'warning';
    }
  };

  const getSourceColor = (source: string) => {
    switch (source) {
      case 'LinkedIn':
        return 'primary';
      case 'Mail':
        return 'info';
      case 'Phone':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const stats = useMemo(() => {
    return {
      total: filteredRDVs.length,
      valides: filteredRDVs.filter(rdv => rdv.etat === 'Validé').length,
      enAttente: filteredRDVs.filter(rdv => rdv.etat === 'En attente').length,
      disqualifies: filteredRDVs.filter(rdv => rdv.etat === 'Disqualifié').length,
      noShow: filteredRDVs.filter(rdv => rdv.etat === 'No show').length,
      litige: filteredRDVs.filter(rdv => rdv.etat === 'Litige').length,
    };
  }, [filteredRDVs]);

  if (loading && rdvLeads.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        {/* En-tête avec statistiques */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h4" gutterBottom>
              Gestion des RDV
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6">{stats.total}</Typography>
              <Typography variant="body2" color="textSecondary">Total</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="success.main">{stats.valides}</Typography>
              <Typography variant="body2" color="textSecondary">Validés</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="warning.main">{stats.enAttente}</Typography>
              <Typography variant="body2" color="textSecondary">En attente</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="error.main">{stats.disqualifies}</Typography>
              <Typography variant="body2" color="textSecondary">Disqualifiés</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="error.main">{stats.noShow}</Typography>
              <Typography variant="body2" color="textSecondary">No show</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="error.main">{stats.litige}</Typography>
              <Typography variant="body2" color="textSecondary">Litige</Typography>
            </Box>
          </Box>
        </Box>

        {/* Message d'erreur */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Filtres */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <ToggleButtonGroup
              value={etatFilter}
              exclusive
              onChange={(_, newEtat) => setEtatFilter(newEtat || 'tous')}
            >
              <ToggleButton value="tous">Tous</ToggleButton>
              <ToggleButton value="Validé">Validé</ToggleButton>
              <ToggleButton value="En attente">En attente</ToggleButton>
              <ToggleButton value="No show">No show</ToggleButton>
              <ToggleButton value="Litige">Litige</ToggleButton>
              <ToggleButton value="Disqualifié">Disqualifié</ToggleButton>
            </ToggleButtonGroup>

            <TextField
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <PeriodFilter
            period={period}
            onPeriodChange={setPeriod}
            startDate={startDate}
            endDate={endDate}
            onDateChange={handleDateChange}
          />
        </Box>

        {/* Tableau */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>État</TableCell>
                <TableCell>Prénom</TableCell>
                <TableCell>Nom</TableCell>
                <TableCell>Entreprise</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Téléphone</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>LinkedIn</TableCell>
                <TableCell>Qualification</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRDVs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <Typography variant="body1" color="textSecondary" sx={{ py: 3 }}>
                      Aucun RDV trouvé pour les filtres sélectionnés
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRDVs.map((rdv) => (
                  <TableRow key={rdv.id} hover>
                    <TableCell>
                      <Chip
                        label={rdv.etat}
                        color={getEtatColor(rdv.etat)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{rdv.prenom}</TableCell>
                    <TableCell>{rdv.nom}</TableCell>
                    <TableCell>{rdv.entreprise}</TableCell>
                    <TableCell>
                      {format(new Date(rdv.date), 'dd/MM/yyyy HH:mm', { locale: fr })}
                    </TableCell>
                    <TableCell>{rdv.telephone}</TableCell>
                    <TableCell>{rdv.email}</TableCell>
                    <TableCell>
                      {rdv.linkedin ? (
                      <IconButton
                        href={rdv.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        color="primary"
                      >
                        <LinkedIn />
                      </IconButton>
                      ) : (
                        <span>-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Visibility />}
                          onClick={() => handleOpenQualificationModal(rdv)}
                        >
                          Voir
                        </Button>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={rdv.source}
                        color={getSourceColor(rdv.source)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {isRdvExpired(rdv.date) ? (
                          // RDV dépassé (24h passées) - affiche toujours "Expiré" même s'il a été qualifié automatiquement
                          <Tooltip title="RDV dépassé - Plus de 24h depuis la date du RDV">
                            <Button
                              color="warning"
                              variant="outlined"
                              size="small"
                              disabled
                              startIcon={<AccessTime />}
                            >
                              Expiré
                            </Button>
                          </Tooltip>
                        ) : rdv.etat !== 'En attente' ? (
                          // RDV qualifié dans les délais
                          <Tooltip title={`RDV ${rdv.etat.toLowerCase()}`}>
                            <Button
                              color="success"
                              variant="outlined"
                              size="small"
                              disabled
                              startIcon={<RateReview />}
                            >
                              {rdv.etat}
                            </Button>
                          </Tooltip>
                        ) : (
                          // RDV en attente dans les délais
                          <Tooltip title="Qualifier le RDV (Validé, Litige, No show)">
                            <Button
                              color="primary"
                              variant="outlined"
                              size="small"
                              onClick={() => handleOpenQualificationActionModal(rdv)}
                              startIcon={<RateReview />}
                            >
                              Qualifier
                            </Button>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      {/* Modal de qualification (vue) */}
      {selectedRdv && (
        <QualificationModal
          open={isModalOpen}
          onClose={handleCloseQualificationModal}
          rdvId={selectedRdv.id}
          qualification={selectedRdv.qualification}
          commentaire={selectedRdv.qualification}
          isQualified={selectedRdv.etat === 'Validé' ? true : selectedRdv.etat === 'Disqualifié' ? false : null}
          leadName={`${selectedRdv.prenom} ${selectedRdv.nom}`}
          onQualificationUpdated={handleQualificationUpdated}
        />
      )}
      
      {/* Modal de qualification avancée (Validé, Litige, No show) */}
      {selectedRdvForAction && (
        <QualificationActionModal
          open={isActionModalOpen}
          onClose={handleCloseQualificationActionModal}
          rdvId={selectedRdvForAction.id}
          leadName={`${selectedRdvForAction.prenom} ${selectedRdvForAction.nom}`}
          currentStatus={selectedRdvForAction.etat}
          onQualificationUpdated={handleQualificationActionCompleted}
        />
      )}
    </Container>
  );
};

export default RDVPage; 